/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PollingStatusType =
  | 'idle'
  | 'polling'
  | 'completed'
  | 'failed'
  | 'timeout';

export type WorkflowHistoryEntry = {
  requestId: string;
  title: string;
  message: string;
  status: 'completed' | 'failed' | 'timeout';
  duration: number;
};

export type ActiveRequestType = {
  id: string;
  title: string;
  startTime: number;
};

export type CreateLevelsRequestType = {
  structureUniqueId: string;
  projectId: number;
  instanceUniqueId: string;
};

export type QueuedRequestType = {
  id: string;
  title: string;
  message: string;
  data: CreateLevelsRequestType;
  type: 'levels';
};

export interface AiState {
  shouldAutoGenerateLevels: boolean;
  levelsLoading: boolean;
  shouldAutoGenerateSpaces: boolean;
  spacesLoading: boolean;
  preventAutoGenerateSpaces: boolean;
  shouldArrangeSpaces: boolean;
  arrangingLoading: boolean;
  activeRequest: ActiveRequestType | null;
  pollingStatus: PollingStatusType;
  workflowHistory: WorkflowHistoryEntry[];
  requestQueue: QueuedRequestType[];
  hasAutoGeneratedConfiguration: boolean;
  configGenerating: boolean;
}

export const defaultAiState: AiState = {
  shouldAutoGenerateLevels: false,
  levelsLoading: false,
  shouldAutoGenerateSpaces: false,
  spacesLoading: false,
  preventAutoGenerateSpaces: true,
  shouldArrangeSpaces: false,
  arrangingLoading: false,
  activeRequest: null,
  pollingStatus: 'idle',
  workflowHistory: [],
  requestQueue: [],
  hasAutoGeneratedConfiguration: false,
  configGenerating: false
};

export const aiSlice = createSlice({
  name: 'ai',
  initialState: defaultAiState,
  reducers: {
    setShouldAutoGenerateLevels: (state, action: PayloadAction<boolean>) => {
      state.shouldAutoGenerateLevels = action.payload;
    },
    setLevelsLoading: (state, action: PayloadAction<boolean>) => {
      state.levelsLoading = action.payload;
    },
    setShouldAutoGenerateSpaces: (state, action: PayloadAction<boolean>) => {
      state.shouldAutoGenerateSpaces = action.payload;
    },
    setSpacesLoading: (state, action: PayloadAction<boolean>) => {
      state.spacesLoading = action.payload;
    },
    setPreventAutoGenerateSpaces: (state, action: PayloadAction<boolean>) => {
      state.preventAutoGenerateSpaces = action.payload;
    },
    setShouldArrangeSpaces: (state, action: PayloadAction<boolean>) => {
      state.shouldArrangeSpaces = action.payload;
    },
    setArrangingLoading: (state, action: PayloadAction<boolean>) => {
      state.arrangingLoading = action.payload;
    },
    setActiveRequest: (
      state,
      action: PayloadAction<ActiveRequestType | null>
    ) => {
      state.activeRequest = action.payload;
    },
    setPollingStatus: (state, action: PayloadAction<PollingStatusType>) => {
      state.pollingStatus = action.payload;
    },
    clearActiveRequest: (state) => {
      state.activeRequest = null;
      state.pollingStatus = 'idle';
    },
    addWorkflowToHistory: (
      state,
      action: PayloadAction<WorkflowHistoryEntry>
    ) => {
      state.workflowHistory = [action.payload, ...state.workflowHistory].slice(
        0,
        10
      );
    },
    addToQueue: (state, action: PayloadAction<QueuedRequestType>) => {
      state.requestQueue.push(action.payload);
    },
    removeFromQueue: (state) => {
      state.requestQueue.shift();
    },
    clearQueue: (state) => {
      state.requestQueue = [];
    },
    setHasAutoGeneratedConfiguration: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.hasAutoGeneratedConfiguration = action.payload;
    },
    setConfigGenerating: (state, action: PayloadAction<boolean>) => {
      state.configGenerating = action.payload;
    }
  }
});

export const {
  setShouldAutoGenerateLevels,
  setLevelsLoading,
  setShouldAutoGenerateSpaces,
  setSpacesLoading,
  setPreventAutoGenerateSpaces,
  setShouldArrangeSpaces,
  setArrangingLoading,
  setActiveRequest,
  setPollingStatus,
  clearActiveRequest,
  addWorkflowToHistory,
  addToQueue,
  removeFromQueue,
  clearQueue,
  setHasAutoGeneratedConfiguration,
  setConfigGenerating
} = aiSlice.actions;

export default aiSlice.reducer;
